/**
 * Amplitude Session Replay Integration with Segment
 *
 * This module integrates Amplitude Session Replay with the existing Segment implementation.
 * It follows the Amplitude (Actions) destination pattern.
 *
 * @see https://amplitude.com/docs/session-replay/session-replay-integration-with-segment#amplitude-actions-destination
 */

import * as sessionReplay from '@amplitude/session-replay-browser'

// Cookie utility functions
const cookie = {
  get: (name) => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(';').shift()
    return null
  },
  set: (name, value) => {
    document.cookie = `${name}=${value}; path=/; max-age=31536000; SameSite=Lax`
  },
  remove: (name) => {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`
  },
}

// Click tracking constants
const CLICK_DATA_KEY = 'session_replay_click_data'
const CLICK_THRESHOLD = 5
const THIRTY_MINUTES_MS = 30 * 60 * 1000

/**
 * Get click tracking data from localStorage
 * @returns {object|null} Click data with clickCount, firstClickTimestamp, and recordingStarted
 */
const getClickData = () => {
  try {
    const data = localStorage.getItem(CLICK_DATA_KEY)
    if (!data) return null
    return JSON.parse(data)
  } catch {
    return null
  }
}

/**
 * Save click tracking data to localStorage
 * @param {object} data - Click data to save
 */
const setClickData = (data) => {
  try {
    localStorage.setItem(CLICK_DATA_KEY, JSON.stringify(data))
  } catch {
    // localStorage might be full or disabled
  }
}

/**
 * Clear click tracking data
 */
const clearClickData = () => {
  try {
    localStorage.removeItem(CLICK_DATA_KEY)
  } catch {
    // Ignore errors
  }
}

/**
 * Check if timestamp is older than 30 minutes
 * @param {number} timestamp - Unix timestamp to check
 * @returns {boolean} True if expired
 */
const isClickDataExpired = (timestamp) => {
  return Date.now() - timestamp > THIRTY_MINUTES_MS
}

/**
 * Check if current page is a login/signup page where we shouldn't track clicks
 * @returns {boolean} True if on login/signup page
 */
const isLoginPage = () => {
  const currentPath = window.location.pathname.toLowerCase()

  // These are exact login/signup related paths
  const loginPaths = [
    '/login',
    '/signup',
    '/getstarted',
    '/adminsignup',
    '/studentsignup',
    '/resetpassword',
    '/partnerlogin',
    '/districtlogin',
  ]

  // Check for exact root path (/) which is also login
  if (currentPath === '/') return true

  // Check for SSO auth paths (but NOT /author/ which is a valid app path)
  // Only match paths like /auth, /authorize, /oauth etc.
  if (
    currentPath.startsWith('/auth') ||
    currentPath.includes('/oauth') ||
    currentPath.includes('/authorize')
  ) {
    // Exclude /author/ paths which are valid logged-in user paths
    if (currentPath.startsWith('/author')) {
      return false
    }
    return true
  }

  return loginPaths.some((path) => currentPath.includes(path))
}

// Get the stored session ID from Amplitude's cookie
const getStoredSessionId = () => {
  return cookie.get('analytics_session_id') || 0
}

// Generate a UUID v4
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16)
    const v = c === 'x' ? r : (r % 4) + 8
    return v.toString(16)
  })
}

/**
 * Flag to track if Session Replay has been started
 * Prevents duplicate initialization
 */
let sessionReplayStarted = false

/**
 * Check if Session Replay should be blocked on current page
 * Returns true if page contains sensitive student data
 */
const shouldBlockSessionReplay = (debugMode = false) => {
  const currentPath = window.location.pathname

  // Block Session Replay on pages that display student PII
  const blockedPaths = [
    '/home/', // All student-facing pages (e.g., /home/assignments, /home/classes)
    // Add more paths as needed
  ]

  const isBlocked = blockedPaths.some((path) => currentPath.includes(path))

  if (debugMode) {
    console.log('[Session Replay] 🔍 Path check:', {
      currentPath,
      isBlocked,
      blockedPaths,
    })
  }

  return isBlocked
}

// Store references to original history methods for cleanup
let originalPushState = null
let originalReplaceState = null
let popstateHandler = null

// ID for the full-page privacy overlay
const PRIVACY_OVERLAY_ID = 'amp-session-replay-privacy-overlay'

/**
 * Add a full-page privacy overlay to block recording on sensitive pages
 * This overlay uses amp-block class so the SDK won't capture any content beneath it
 */
const addPrivacyOverlay = (debugMode = false) => {
  if (typeof document === 'undefined') return

  // Don't add if already exists
  if (document.getElementById(PRIVACY_OVERLAY_ID)) {
    if (debugMode) {
      console.log('[Session Replay] 🛡️ Privacy overlay already exists')
    }
    return
  }

  const overlay = document.createElement('div')
  overlay.id = PRIVACY_OVERLAY_ID
  overlay.className = 'amp-block' // This class tells Amplitude to block this element entirely
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 2147483647;
    pointer-events: none;
    background: transparent;
  `
  // Add a data attribute to indicate this is for privacy
  overlay.setAttribute('data-amp-privacy', 'blocked-page')

  document.body.appendChild(overlay)

  if (debugMode) {
    console.log(
      '[Session Replay] 🛡️ Privacy overlay added - page content will be blocked from recording'
    )
  }
}

/**
 * Remove the privacy overlay when navigating to allowed pages
 */
const removePrivacyOverlay = (debugMode = false) => {
  if (typeof document === 'undefined') return

  const overlay = document.getElementById(PRIVACY_OVERLAY_ID)
  if (overlay) {
    overlay.remove()
    if (debugMode) {
      console.log(
        '[Session Replay] ✅ Privacy overlay removed - recording normally'
      )
    }
  }
}

/**
 * Setup History API listeners for instant route change detection
 * This is more efficient than setInterval polling
 *
 * @param {Function} onRouteChange - Callback to fire when route changes
 */
const setupHistoryListeners = (onRouteChange) => {
  if (typeof window === 'undefined' || typeof window.history === 'undefined') {
    return
  }

  // Store original methods for cleanup
  originalPushState = window.history.pushState.bind(window.history)
  originalReplaceState = window.history.replaceState.bind(window.history)

  // Wrap pushState - fires on programmatic navigation (React Router, etc.)
  window.history.pushState = function wrappedPushState(...args) {
    originalPushState(...args)
    // Use setTimeout to ensure the URL has updated before checking
    setTimeout(onRouteChange, 0)
  }

  // Wrap replaceState - fires on URL replacement
  window.history.replaceState = function wrappedReplaceState(...args) {
    originalReplaceState(...args)
    setTimeout(onRouteChange, 0)
  }

  // Listen for back/forward button clicks
  popstateHandler = onRouteChange
  window.addEventListener('popstate', popstateHandler)
}

/**
 * Cleanup History API listeners and restore original methods
 */
const cleanupHistoryListeners = () => {
  if (typeof window === 'undefined' || typeof window.history === 'undefined') {
    return
  }

  // Restore original history methods
  if (originalPushState) {
    window.history.pushState = originalPushState
    originalPushState = null
  }

  if (originalReplaceState) {
    window.history.replaceState = originalReplaceState
    originalReplaceState = null
  }

  // Remove popstate listener
  if (popstateHandler) {
    window.removeEventListener('popstate', popstateHandler)
    popstateHandler = null
  }
}

/**
 * Monitor route changes and add/remove privacy overlay based on current page
 * Uses History API for instant detection instead of polling
 *
 * IMPORTANT: We use a privacy overlay approach instead of stopping/restarting recording because:
 * 1. sessionReplay.shutdown() doesn't immediately stop - it buffers data that still gets sent
 * 2. Restarting creates a new session which breaks session continuity
 * 3. The overlay approach blocks content at the DOM level BEFORE the SDK captures it
 */
const setupRouteMonitoring = (
  amplitudeApiKey,
  segmentAnalytics,
  amplitudeSampleRate,
  debugMode = false
) => {
  let currentPath = window.location.pathname
  let isCurrentlyBlocked = shouldBlockSessionReplay(debugMode)

  // If we're starting on a blocked page, add the privacy overlay immediately
  if (isCurrentlyBlocked) {
    addPrivacyOverlay(debugMode)
  }

  if (debugMode) {
    console.log(
      '[Session Replay] 🔍 Route monitoring active (using History API)'
    )
    console.log(
      '[Session Replay] 🔍 Initial state:',
      isCurrentlyBlocked ? 'BLOCKED (overlay active)' : 'ALLOWED'
    )
  }

  // Route change handler - fires instantly on navigation
  const handleRouteChange = () => {
    const newPath = window.location.pathname

    if (newPath !== currentPath) {
      currentPath = newPath
      const shouldBlock = shouldBlockSessionReplay(debugMode)

      // Route changed to a blocked page - add privacy overlay
      if (shouldBlock && !isCurrentlyBlocked) {
        if (debugMode) {
          console.log(
            '[Session Replay] ⛔ Navigated to blocked page - adding privacy overlay'
          )
        }
        isCurrentlyBlocked = true
        addPrivacyOverlay(debugMode)
      }
      // Route changed from blocked page to allowed page - remove privacy overlay
      else if (!shouldBlock && isCurrentlyBlocked) {
        if (debugMode) {
          console.log(
            '[Session Replay] ✅ Navigated to allowed page - removing privacy overlay'
          )
        }
        isCurrentlyBlocked = false
        removePrivacyOverlay(debugMode)
      }
    }
  }

  // Setup History API listeners for instant route detection
  setupHistoryListeners(handleRouteChange)
}

/**
 * Start the actual session replay recording
 * This is called after click threshold is met or if recording was already started
 */
const startSessionReplayRecording = async (
  amplitudeApiKey,
  segmentAnalytics,
  amplitudeSampleRate,
  debugMode
) => {
  // Prevent duplicate initialization
  if (sessionReplayStarted) {
    if (debugMode) {
      console.log('[Session Replay] ⏭️ Already started, skipping...')
    }
    return
  }

  sessionReplayStarted = true

  // Check if we should block Session Replay on this page
  if (shouldBlockSessionReplay(debugMode)) {
    if (debugMode) {
      console.log(
        '[Session Replay] ⛔ Blocked on this page (contains student PII)'
      )
    }
    // Still setup route monitoring even if blocked initially
    // So it can restart when navigating to allowed pages
    setupRouteMonitoring(
      amplitudeApiKey,
      segmentAnalytics,
      amplitudeSampleRate,
      debugMode
    )
    return
  }

  if (!amplitudeApiKey) {
    console.warn(
      '[Session Replay] ⚠️ Amplitude API Key not provided. Session Replay will not be initialized.'
    )
    return
  }

  if (!segmentAnalytics) {
    console.error(
      '[Session Replay] ❌ Segment analytics instance not provided.'
    )
    return
  }

  try {
    const storedSessionId = getStoredSessionId()

    // Get device ID from Segment's internal state
    let deviceId

    // Try to get anonymousId from Segment (it may be set after the script loads)
    if (typeof segmentAnalytics.user === 'function') {
      // Standard Segment Analytics.js
      const user = await segmentAnalytics.user()
      deviceId = user.anonymousId()
    } else if (segmentAnalytics._user && segmentAnalytics._user.anonymousId) {
      // Alternative internal reference
      deviceId = segmentAnalytics._user.anonymousId()
    } else {
      // Fallback: generate or retrieve device ID from localStorage
      const storedDeviceId = localStorage.getItem('ajs_anonymous_id')
      if (storedDeviceId) {
        // Remove quotes if present
        deviceId = storedDeviceId.replace(/^"|"$/g, '')
      } else {
        // Generate a new device ID if none exists
        deviceId = generateUUID()
        localStorage.setItem('ajs_anonymous_id', `"${deviceId}"`)
      }
    }

    if (debugMode) {
      console.log('[Session Replay] Using device ID:', deviceId)
      console.log('[Session Replay] Using session ID:', storedSessionId)
    }

    // Initialize Session Replay with the current session ID and device ID
    const initConfig = {
      deviceId,
    }

    // Generate a session ID if none exists
    let sessionId = storedSessionId

    if (!sessionId || sessionId === 0) {
      // Generate a new session ID (Unix timestamp)
      sessionId = Date.now()
      cookie.set('analytics_session_id', sessionId)
      if (debugMode) {
        console.log('[Session Replay] 📝 Generated new session ID:', sessionId)
      }
    } else if (debugMode) {
      console.log('[Session Replay] 📝 Using existing session ID:', sessionId)
    }

    initConfig.sessionId = sessionId

    // Add sample rate if provided
    if (amplitudeSampleRate && amplitudeSampleRate !== '1.0') {
      initConfig.sampleRate = parseFloat(amplitudeSampleRate)
    }

    // Privacy: Mask elements containing student PII
    initConfig.maskTextClass = 'amp-mask'
    initConfig.maskAllInputs = true
    initConfig.blockClass = 'amp-block'

    if (debugMode) {
      console.log('[Session Replay] Initializing with config:', initConfig)
    }

    // CRITICAL: This initializes and STARTS the session replay recording automatically
    // Recording begins immediately after init() completes - it does NOT require any track events
    // The recording captures all user interactions, DOM changes, and page activity
    // Add timeout to prevent hanging if SDK has issues
    const INIT_TIMEOUT_MS = 15000 // 15 seconds
    let result
    try {
      const initPromise = sessionReplay.init(amplitudeApiKey, initConfig)
        .promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error('Session Replay init() timeout after 15s')),
          INIT_TIMEOUT_MS
        )
      })

      result = await Promise.race([initPromise, timeoutPromise])
    } catch (error) {
      // Handle timeout or other init errors gracefully
      console.error(
        '[Session Replay] ⚠️ Init failed or timed out - continuing without recording:',
        error.message
      )
      // Don't throw - prevents hanging promise (login already non-blocking with fork)
      sessionReplayStarted = false // Reset flag so it can retry
      return
    }

    if (debugMode) {
      console.log('[Session Replay] Init result:', result)

      // Verify initialization
      const props = sessionReplay.getSessionReplayProperties()
      console.log('[Session Replay] Properties after init:', props)

      if (!props || Object.keys(props).length === 0) {
        console.warn('[Session Replay] ⚠️ Initialized but properties are empty')
        console.warn(
          '[Session Replay]    This might be normal - properties may be set after first event'
        )
      }
    }

    // Expose sessionReplay globally for testing and debugging (only in debug mode)
    if (debugMode) {
      window.sessionReplay = sessionReplay
      window.amplitudeSessionReplayDebug = {
        getProperties: () => sessionReplay.getSessionReplayProperties(),
        getDeviceId: () => localStorage.getItem('ajs_anonymous_id'),
        getSessionId: () => cookie.get('analytics_session_id'),
        setSessionId: (id) => sessionReplay.setSessionId(id),
        config: {
          apiKey: amplitudeApiKey ? '✅ Set' : '❌ Missing',
          sampleRate: amplitudeSampleRate,
          deviceId: initConfig.deviceId,
          sessionId: initConfig.sessionId,
        },
        test: async () => {
          console.group('🧪 Session Replay Test')
          console.log('✅ Session Replay initialized')

          const propsBeforeEvent = sessionReplay.getSessionReplayProperties()
          console.log('Properties (before event):', propsBeforeEvent)
          console.log('Device ID:', localStorage.getItem('ajs_anonymous_id'))
          console.log(
            'Session ID (cookie):',
            cookie.get('analytics_session_id')
          )
          console.log('Session ID (stored):', getStoredSessionId())
          console.log('Config:', {
            apiKey: amplitudeApiKey ? '✅ Set' : '❌ Missing',
            sampleRate: amplitudeSampleRate,
            deviceId: initConfig.deviceId,
            sessionId: initConfig.sessionId,
          })

          // Try tracking a test event and check if properties appear
          if (window.analytics && window.analytics.track) {
            console.log('\n📤 Tracking test event to trigger session...')
            window.analytics.track('Session Replay Test Event', {
              timestamp: new Date().toISOString(),
              test: true,
            })

            // Wait a bit for the event to process
            await new Promise((resolve) => setTimeout(resolve, 1000))

            const propsAfterEvent = sessionReplay.getSessionReplayProperties()
            console.log('Properties (after event):', propsAfterEvent)

            if (propsAfterEvent && Object.keys(propsAfterEvent).length > 0) {
              console.log('✅ Session Replay properties populated after event!')
            } else {
              console.warn('⚠️ Properties still empty after event')
            }
          }

          console.groupEnd()
        },
      }
      console.log(
        '[Session Replay] 🐛 Debug mode enabled - window.sessionReplay and window.amplitudeSessionReplayDebug are available'
      )
      console.log(
        '[Session Replay]    Run window.amplitudeSessionReplayDebug.test() to run diagnostics'
      )
    } else {
      // In production, still expose sessionReplay for proper functionality
      window.sessionReplay = sessionReplay
    }

    if (debugMode) {
      console.log(
        '[Session Replay] ✅ Amplitude Session Replay initialized successfully'
      )
    }

    // Add middleware to check if the session ID has changed
    segmentAnalytics.addSourceMiddleware(({ payload, next }) => {
      try {
        const currentStoredSessionId = getStoredSessionId()

        // Check for Amplitude Actions session ID in different possible locations
        const nextSessionId =
          payload.obj.integrations?.['Actions Amplitude']?.session_id ||
          payload.obj.integrations?.['Amplitude (Actions)']?.session_id ||
          payload.obj.integrations?.Amplitude?.session_id ||
          0

        if (debugMode) {
          console.log('[Session Replay] 🔍 Middleware - Session ID check:', {
            stored: currentStoredSessionId,
            next: nextSessionId,
            integrations: payload.obj.integrations,
          })
        }

        if (
          nextSessionId &&
          nextSessionId > 0 &&
          currentStoredSessionId < nextSessionId
        ) {
          cookie.set('analytics_session_id', nextSessionId)
          sessionReplay.setSessionId(nextSessionId)

          if (debugMode) {
            console.log(
              '[Session Replay] 🔄 Session ID updated:',
              nextSessionId
            )
            const updatedProps = sessionReplay.getSessionReplayProperties()
            console.log('[Session Replay]    Updated properties:', updatedProps)
          }
        }
      } catch (error) {
        console.error('[Session Replay] Error updating session ID:', error)
      }
      next(payload)
    })

    // Wrap track and page methods directly using Object.defineProperty
    if (debugMode) {
      console.log(
        '[Session Replay] 🔧 Wrapping Segment methods to inject Session Replay ID...'
      )
    }

    // CRITICAL: Wrap window.analytics, not just the local reference
    const analytics = window.analytics
    const originalTrack = analytics.track.bind(analytics)
    const originalPage = analytics.page.bind(analytics)
    const originalIdentify = analytics.identify.bind(analytics)

    // Wrap methods - use writable: true so debugger can wrap them too
    window.analytics.track = function wrappedTrack(
      event,
      properties,
      options,
      callback
    ) {
      const sessionReplayProperties = sessionReplay.getSessionReplayProperties()
      const enrichedProperties = {
        ...properties,
        ...sessionReplayProperties,
      }

      if (debugMode) {
        console.log(
          '[Session Replay] ✅ Track:',
          event,
          sessionReplayProperties['[Amplitude] Session Replay ID']
        )
      }

      return originalTrack(event, enrichedProperties, options, callback)
    }

    window.analytics.page = function wrappedPage(
      category,
      name,
      properties,
      options,
      callback
    ) {
      // Handle overloaded page() method
      let finalCategory
      let finalName
      let finalProperties
      let finalOptions
      let finalCallback

      if (typeof category === 'object') {
        finalProperties = category
        finalOptions = name
        finalCallback = properties
      } else if (typeof name === 'object') {
        finalCategory = category
        finalProperties = name
        finalOptions = properties
        finalCallback = options
      } else {
        finalCategory = category
        finalName = name
        finalProperties = properties
        finalOptions = options
        finalCallback = callback
      }

      const sessionReplayProperties = sessionReplay.getSessionReplayProperties()
      const enrichedProperties = {
        ...finalProperties,
        ...sessionReplayProperties,
      }

      if (debugMode) {
        console.log(
          '[Session Replay] ✅ Page:',
          sessionReplayProperties['[Amplitude] Session Replay ID']
        )
      }

      return originalPage(
        finalCategory,
        finalName,
        enrichedProperties,
        finalOptions,
        finalCallback
      )
    }

    window.analytics.identify = function wrappedIdentify(
      userId,
      traits,
      options,
      callback
    ) {
      const sessionReplayProperties = sessionReplay.getSessionReplayProperties()
      const enrichedTraits = {
        ...traits,
        ...sessionReplayProperties,
      }

      if (debugMode) {
        console.log(
          '[Session Replay] ✅ Identify:',
          userId,
          sessionReplayProperties['[Amplitude] Session Replay ID']
        )
      }

      return originalIdentify(userId, enrichedTraits, options, callback)
    }

    if (debugMode) {
      console.log(
        '[Session Replay] ✅ Methods wrapped successfully (track, page, identify)'
      )
      console.log(
        '[Session Replay] 🔍 Verify: window.analytics.track === wrapped?',
        window.analytics.track.toString().includes('Session Replay')
      )
      console.log(
        '[Session Replay] ✅ Segment middleware for Session Replay added successfully'
      )
    }

    // Setup route monitoring to stop/start recording based on navigation
    setupRouteMonitoring(
      amplitudeApiKey,
      segmentAnalytics,
      amplitudeSampleRate,
      debugMode
    )

    if (debugMode) {
      console.log('[Session Replay] 🔍 Route monitoring active')
    }
  } catch (error) {
    console.error(
      '[Session Replay] ❌ Failed to initialize Amplitude Session Replay:',
      error
    )
    console.error('[Session Replay] Error details:', error.message)
    console.error('[Session Replay] Stack trace:', error.stack)
    sessionReplayStarted = false // Reset flag on error
    throw error // Re-throw to be caught by caller
  }
}

// User roles that should be completely excluded from Session Replay
// These users won't have any recording at all (saves Amplitude resources)
const EXCLUDED_USER_ROLES = ['student']

/**
 * Initialize Amplitude Session Replay with click-based activation
 * Recording starts only after 5 user clicks (persisted for 30 minutes from first click)
 *
 * @param {string} amplitudeApiKey - Amplitude API Key (must match Segment's Amplitude destination)
 * @param {object} segmentAnalytics - The Segment analytics instance
 * @param {number} amplitudeSampleRate - Sample rate for session replay (0.0 to 1.0)
 * @param {boolean} debugMode - Enable debug mode (exposes window.sessionReplay for testing)
 * @param {string} userRole - The role of the current user (e.g., 'teacher', 'student', 'admin')
 * @returns {Promise<void>}
 */
export const initializeSessionReplay = async (
  amplitudeApiKey,
  segmentAnalytics,
  amplitudeSampleRate = 1.0,
  debugMode = false,
  userRole = null
) => {
  // Completely skip Session Replay for excluded user roles (e.g., students)
  // This saves Amplitude resources - no SDK initialization, no recording at all
  if (userRole && EXCLUDED_USER_ROLES.includes(userRole.toLowerCase())) {
    if (debugMode) {
      console.log(
        `[Session Replay] ⏭️ Skipping for ${userRole} user - Session Replay disabled for this role`
      )
    }
    return
  }

  // Always expose debug helper for troubleshooting (even in production)
  window.sessionReplayClickData = {
    get: getClickData,
    clear: clearClickData,
    threshold: CLICK_THRESHOLD,
    timeoutMins: 30,
    isLoginPage: isLoginPage(),
    isBlocked: shouldBlockSessionReplay(false),
    debugMode,
    userRole,
  }

  if (debugMode) {
    console.log('[Session Replay] 🚀 initializeSessionReplay called', {
      path: window.location.pathname,
      hasApiKey: !!amplitudeApiKey,
      hasAnalytics: !!segmentAnalytics,
      sampleRate: amplitudeSampleRate,
      debugMode,
      userRole,
      isLoginPage: isLoginPage(),
      isBlocked: shouldBlockSessionReplay(false),
    })
  }

  // Don't initialize on login pages - wait until user is logged in
  if (isLoginPage()) {
    if (debugMode) {
      console.log(
        '[Session Replay] ⏳ On login page - waiting for login before tracking clicks',
        { path: window.location.pathname }
      )
    }
    return
  }

  // Check if we're starting on a blocked page
  // We still initialize Session Replay but with a privacy overlay
  // This ensures recording works when user navigates to allowed pages
  const isBlockedPage = shouldBlockSessionReplay(debugMode)

  if (isBlockedPage) {
    if (debugMode) {
      console.log(
        '[Session Replay] ⛔ Starting on blocked page - will add privacy overlay'
      )
    }
    // Add privacy overlay immediately to block content capture
    addPrivacyOverlay(debugMode)
  }

  if (!amplitudeApiKey) {
    console.warn(
      '[Session Replay] ⚠️ Amplitude API Key not provided. Session Replay will not be initialized.'
    )
    return
  }

  if (!segmentAnalytics) {
    console.error(
      '[Session Replay] ❌ Segment analytics instance not provided.'
    )
    return
  }

  // Check existing click data
  const clickData = getClickData()

  if (debugMode) {
    console.log('[Session Replay] 📊 Checking existing click data:', clickData)
  }

  // If we have existing data, check if it's still valid (within 30 mins from first click)
  if (clickData) {
    const isExpired = isClickDataExpired(clickData.firstClickTimestamp)
    const timeElapsed = Date.now() - clickData.firstClickTimestamp
    const timeRemaining = Math.round(
      (THIRTY_MINUTES_MS - timeElapsed) / 1000 / 60
    )

    if (debugMode) {
      console.log('[Session Replay] 📊 Click data status:', {
        clickCount: clickData.clickCount,
        threshold: CLICK_THRESHOLD,
        recordingStarted: clickData.recordingStarted,
        isExpired,
        timeElapsedMins: Math.round(timeElapsed / 1000 / 60),
        timeRemainingMins: isExpired ? 0 : timeRemaining,
        firstClickTime: new Date(clickData.firstClickTimestamp).toISOString(),
      })
    }

    if (isExpired) {
      // Data expired - clear it and start fresh
      clearClickData()
      if (debugMode) {
        console.log(
          '[Session Replay] 🕐 Click data expired (30 min window) - resetting'
        )
      }
    } else if (clickData.recordingStarted) {
      // Recording was already started and is still valid - continue recording
      if (debugMode) {
        console.log(
          '[Session Replay] ✅ Recording already started in previous session - continuing immediately (no new clicks needed)',
          {
            clickCount: clickData.clickCount,
            firstClickTime: new Date(
              clickData.firstClickTimestamp
            ).toISOString(),
            timeRemainingMins: timeRemaining,
          }
        )
      }
      await startSessionReplayRecording(
        amplitudeApiKey,
        segmentAnalytics,
        amplitudeSampleRate,
        debugMode
      )
      return
    } else if (clickData.clickCount >= CLICK_THRESHOLD) {
      // Threshold already reached but recording not started yet - start now
      if (debugMode) {
        console.log(
          '[Session Replay] ✅ Click threshold already reached - starting recording'
        )
      }
      setClickData({
        ...clickData,
        recordingStarted: true,
      })
      await startSessionReplayRecording(
        amplitudeApiKey,
        segmentAnalytics,
        amplitudeSampleRate,
        debugMode
      )
      return
    }
  }

  // Set up click tracking
  if (debugMode) {
    console.log(
      `[Session Replay] 👆 Setting up click tracking - need ${CLICK_THRESHOLD} clicks to start recording`
    )
    const currentData = getClickData()
    if (currentData) {
      console.log(
        `[Session Replay] 📊 Current click count: ${currentData.clickCount}/${CLICK_THRESHOLD}`,
        currentData
      )
    } else {
      console.log('[Session Replay] 📊 No existing click data - starting fresh')
    }
  }

  // Prevent duplicate listeners
  if (window._sessionReplayClickListener) {
    if (debugMode) {
      console.log(
        '[Session Replay] 👆 Click listener already set up - skipping'
      )
    }
    return
  }

  // Store debugMode flag globally for the click handler to access
  window._sessionReplayDebugMode = debugMode

  // Track last click timestamp for debouncing
  let lastClickTime = 0
  const DEBOUNCE_MS = 300 // Ignore clicks within 300ms of each other

  const handleClick = async (event) => {
    const isDebug = window._sessionReplayDebugMode
    const now = Date.now()

    // Debounce: ignore rapid clicks (within 300ms)
    if (now - lastClickTime < DEBOUNCE_MS) {
      if (isDebug) {
        console.log(
          '[Session Replay] 👆 Click ignored - debounced (too fast)',
          { timeSinceLastClick: now - lastClickTime }
        )
      }
      return
    }
    lastClickTime = now

    // Don't track clicks on login pages
    if (isLoginPage()) {
      if (isDebug) {
        console.log('[Session Replay] 👆 Click ignored - on login page')
      }
      return
    }

    const currentData = getClickData()

    let newClickData

    if (!currentData || isClickDataExpired(currentData.firstClickTimestamp)) {
      // First click or expired - start fresh
      newClickData = {
        clickCount: 1,
        firstClickTimestamp: now,
        lastClickTimestamp: now,
        recordingStarted: false,
      }
      if (isDebug) {
        console.log(
          '[Session Replay] 👆 First click detected - starting 30 min window',
          {
            timestamp: new Date(now).toISOString(),
            target: event?.target?.tagName,
          }
        )
      }
    } else if (currentData.recordingStarted) {
      // Recording already started - no need to track
      if (isDebug) {
        console.log(
          '[Session Replay] 👆 Click ignored - recording already started'
        )
      }
      return
    } else {
      // Increment click count
      newClickData = {
        ...currentData,
        clickCount: currentData.clickCount + 1,
        lastClickTimestamp: now,
      }
    }

    setClickData(newClickData)

    if (isDebug) {
      const timeRemaining = Math.round(
        (THIRTY_MINUTES_MS - (now - newClickData.firstClickTimestamp)) /
          1000 /
          60
      )
      console.log(
        `[Session Replay] 👆 Click registered: ${newClickData.clickCount}/${CLICK_THRESHOLD}`,
        {
          clickCount: newClickData.clickCount,
          threshold: CLICK_THRESHOLD,
          timeRemainingMins: timeRemaining,
          firstClickTime: new Date(
            newClickData.firstClickTimestamp
          ).toISOString(),
          target: event?.target?.tagName,
          targetClass: event?.target?.className?.slice?.(0, 50),
        }
      )
    }

    // Check if we've reached the threshold
    if (newClickData.clickCount >= CLICK_THRESHOLD) {
      if (isDebug) {
        console.log(
          '[Session Replay] 🎉 Click threshold reached! Starting recording...'
        )
      }

      // Mark recording as started
      setClickData({
        ...newClickData,
        recordingStarted: true,
      })

      // Remove click listener - no longer needed
      document.removeEventListener('click', handleClick, true)
      delete window._sessionReplayClickListener

      if (isDebug) {
        console.log(
          '[Session Replay] 👆 Click listener removed - no longer needed'
        )
      }

      // Start recording
      await startSessionReplayRecording(
        amplitudeApiKey,
        segmentAnalytics,
        amplitudeSampleRate,
        isDebug
      )
    }
  }

  // Add click listener (capture phase to catch all clicks)
  document.addEventListener('click', handleClick, true)
  window._sessionReplayClickListener = handleClick

  if (debugMode) {
    console.log('[Session Replay] 👆 Click listener attached successfully')
    console.log(
      '[Session Replay] 🐛 Debug helper available: window.sessionReplayClickData.get()'
    )
  }
}

/**
 * Get current session replay properties
 * Useful for debugging or manual tracking
 *
 * @returns {object} Session replay properties
 */
export const getSessionReplayProperties = () => {
  try {
    return sessionReplay.getSessionReplayProperties()
  } catch (error) {
    console.error('Error getting session replay properties:', error)
    return {}
  }
}

/**
 * Manually set session ID
 * Use this if you need to sync session IDs manually
 *
 * @param {number} sessionId - The new session ID
 */
export const setSessionId = (sessionId) => {
  try {
    sessionReplay.setSessionId(sessionId)
    cookie.set('analytics_session_id', sessionId)
  } catch (error) {
    console.error('[Session Replay] Error setting session ID:', error)
  }
}

/**
 * Shutdown session replay
 * Call this when cleaning up or logging out
 */
export const shutdownSessionReplay = async () => {
  try {
    // Reset the started flag
    sessionReplayStarted = false

    // Cleanup History API listeners (restore original pushState/replaceState)
    cleanupHistoryListeners()

    // Remove privacy overlay if exists
    removePrivacyOverlay()

    // Clear engagement timeout if exists
    if (
      typeof window !== 'undefined' &&
      window._sessionReplayEngagementTimeout
    ) {
      clearTimeout(window._sessionReplayEngagementTimeout)
      delete window._sessionReplayEngagementTimeout
    }

    // Remove click listener if it exists
    if (typeof window !== 'undefined' && window._sessionReplayClickListener) {
      document.removeEventListener(
        'click',
        window._sessionReplayClickListener,
        true
      )
      delete window._sessionReplayClickListener
    }

    if (window.sessionReplay) {
      await sessionReplay.shutdown()
    }
  } catch (error) {
    console.error('[Session Replay] Error shutting down:', error)
  }
}

/**
 * Reset Session Replay on logout
 * This should be called when a user logs out to ensure the next user gets a fresh session
 * Clears session ID cookie, Segment's anonymousId, and click tracking data
 *
 * @returns {void}
 */
export const resetSessionReplay = () => {
  try {
    // Reset the started flag
    sessionReplayStarted = false

    // Remove privacy overlay if exists
    removePrivacyOverlay()

    // Clear the session ID cookie
    cookie.remove('analytics_session_id')

    // Clear Segment's anonymous ID from localStorage (will be regenerated on next login)
    localStorage.removeItem('ajs_anonymous_id')

    // Clear click tracking data
    clearClickData()

    // Remove click listener if it exists
    if (window._sessionReplayClickListener) {
      document.removeEventListener(
        'click',
        window._sessionReplayClickListener,
        true
      )
      delete window._sessionReplayClickListener
    }
  } catch (error) {
    console.error('[Session Replay] ❌ Error resetting Session Replay:', error)
  }
}

/**
 * Check and reset Session Replay if needed on login
 * This is a safety mechanism for cases where logout wasn't properly called
 * (e.g., session timeout, force logout, browser crash)
 *
 * @param {string} currentUserId - The ID of the user currently logging in
 * @returns {void}
 */
export const checkAndResetSessionReplayOnLogin = (currentUserId) => {
  try {
    // Check if there's a stored user ID in localStorage from previous session
    const lastUserId = localStorage.getItem('last_session_replay_user_id')

    // If the current user is different from the last user, reset Session Replay
    if (lastUserId && lastUserId !== currentUserId) {
      resetSessionReplay()
    }

    // Store the current user ID for next login check
    localStorage.setItem('last_session_replay_user_id', currentUserId)
  } catch (error) {
    console.error(
      '[Session Replay] ❌ Error checking Session Replay on login:',
      error
    )
  }
}

export default {
  initializeSessionReplay,
  getSessionReplayProperties,
  setSessionId,
  shutdownSessionReplay,
  resetSessionReplay,
  checkAndResetSessionReplayOnLogin,
}
